
import { SessionManager } from './SessionManager';
import { LoginParams } from '../api/types/LoginParams';
import { SessionInfoResult, SessionInfo } from '../api/types/SessionInfo';
import * as rsa from '../../external/rsa/rsa';
import { WebClientApiResponse } from '../api/WebClientApiResponse';
import {
  WebClientRequesterWrapper, WebClientRequester, WebClientSender,
  VoidWebClientSender
} from '../requester/webClientRequester';
import { WebClientFetchRequester } from '../requester/WebClientFetchRequester';
import { getSuccessResult } from '../api/vizPortalWebClientHelpers';
import { LoginMethod } from '../api/methods/LoginMethod';
import { GeneratePublicKeyMethod, GeneratePublicKeyResult } from '../api/methods/GeneratePublicKeyMethod';
import { LogoutMethod, LogoutResult } from '../api/methods/LogoutMethod';

// Start at 7-digit numbers so these don't collide with server error codes
export enum ErrorReason {
  Canceled = 1000001,
  ResourceNotFound = 1000002,
  Unauthorized = 1000003,
  Unknown = 1000004
};

export enum AuthLevel {
  Anonymous,
  Authenticated,
  SiteAdmin,
  SelfOrAdmin,
  SelfOrServerAdmin,
  ServerAdmin
}

export enum SiteDefaultAuth {
  Local,
  ActiveDirectory,
  SAML,
  External
}

/**
 * This object contains methods for handling a users authentication.
 * It will also update a passed in SessionManager when a user's session is updated
 */
export class AuthenticationManager {

  private generatePublicKey: VoidWebClientSender<WebClientApiResponse<GeneratePublicKeyResult>>;
  private sendLogin: WebClientSender<LoginParams, WebClientApiResponse<SessionInfoResult>>;
  private sendLogout: VoidWebClientSender<WebClientApiResponse<LogoutResult>>;

  private sessionManager: SessionManager;

  public constructor(
    requester: WebClientRequester = new WebClientFetchRequester(),
    sessionManager: SessionManager = new SessionManager(requester),
  ) {
    let wrappedRequester = new WebClientRequesterWrapper(requester);
    this.sessionManager = sessionManager;

    this.generatePublicKey = wrappedRequester.createSender(GeneratePublicKeyMethod);
    this.sendLogin = wrappedRequester.createSender(LoginMethod);
    this.sendLogout = wrappedRequester.createSender(LogoutMethod);
  }

  public login(username: string, password: string, siteUrlName?: string): PromiseLike<SessionInfo|void> {
    return this.sessionManager.sessionInfoRequest(() => {
      return this
        .generatePublicKey()
        .then<GeneratePublicKeyResult>(getSuccessResult)
        .then((generatePublicKeyResult: GeneratePublicKeyResult) =>
          this.sendLogin({
            username,
            encryptedPassword: rsa.encrypt(password, generatePublicKeyResult.key),
            keyId: generatePublicKeyResult.keyId,
            siteUrlName
          })
        )
        .then<SessionInfo>(getSuccessResult);
    });
  }

  public signOut(): PromiseLike<LogoutResult | void> {
    let logoutResultToReturn: LogoutResult | void;

    return this.sessionManager.sessionInfoRequest(() => {
      return this
        .sendLogout()
        .then<LogoutResult>(getSuccessResult)
        .then<void>((logoutResult: LogoutResult) => {
          logoutResultToReturn = logoutResult;
        });
    })
    .then(() => logoutResultToReturn);
  }

  /*
  // The code below was copied from VizPortalClient's ServerService and should be integrated into this class

  public login(username: string, password: string, siteUrlName?: string): ng.IPromise<void> {
    return this.sessionInfoRequest(() => {
      return this.requestPost(new GeneratePublicKeyRequest())
      .then((generatePublicKeyResult: GeneratePublicKeyResult) => {
        var loginParams: LoginParams = {
          username: username,
          encryptedPassword: rsa.encrypt(password, generatePublicKeyResult.key),
          keyId: generatePublicKeyResult.keyId
        };
        if (_.isString(siteUrlName)) {
          loginParams.siteUrlName = siteUrlName;

        }
        var loginRequest = new LoginRequest(loginParams);
        return this.requestPost(loginRequest)
        .then((loginResult: SessionInfoResult) => {
          if (loginResult.errors) {
            return this.$q.reject(loginResult.errors[0]);
          }
          return this.setSessionInfo(loginResult);
        });
      });
    });
  }

  public siteSamlLogin(username: string): ng.IPromise<IGetSiteSamlLoginInfoResult> {
    var loginParams: LoginParams = {
      username: username,
      encryptedPassword: null,
      keyId: null
    };
    return this.requestPost(new GetSiteSamlLoginInfoRequest(loginParams))
    .then((getSiteSamlLoginInfoResult: IGetSiteSamlLoginInfoResult) => {
      if (!getSiteSamlLoginInfoResult) {
        return this.$q.reject(UserErrorCode.UserNotSupportedGuestOperation);
      } else if (getSiteSamlLoginInfoResult.errors) {
        return this.$q.reject(getSiteSamlLoginInfoResult.errors[0]);
      }
      // handle success
      return this.$q.when(getSiteSamlLoginInfoResult);
    }, (error) => { // handle when http status code is not 2xx
      if (error) {
        return this.$q.reject(error);
      }
      return this.$q.reject(UserErrorCode.UserNotSupportedGuestOperation);
    });
  }

  public autoLogin(siteUrlName?: string): ng.IPromise<void> {
    return this.sessionInfoRequest(() => {
      var loginParams: ISiteLoginParams = {};
      if (_.isString(siteUrlName)) {
        loginParams.siteUrlName = siteUrlName;
      }
      var authType = this.authenticationType();
      var loginRequest: VizPortalClientRequest<ISiteLoginParams, IGetSessionInfoResult>;
      switch (authType) {
        case AuthenticationType.Sspi:
          loginRequest = new SspiLoginRequest(loginParams);
          break;
        case AuthenticationType.Kerberos:
          loginRequest = new KerberosLoginRequest(loginParams);
          break;
        case AuthenticationType.ClientCertificate:
        case AuthenticationType.ClientCertificateWithFallback:
          loginRequest = new ClientCertificateLoginRequest(loginParams);
          break;
      }
      // This must be GET to get around an IE "feature". See B144390
      return this.requestGet(loginRequest)
      .then((sessionInfo: SessionInfoResult) => {
        if (sessionInfo.errors) {
          return this.$q.reject(sessionInfo.errors[0] && sessionInfo.errors[0].code);
        }
        return this.setSessionInfo(sessionInfo);
      }, (reason: number) => {
        this.clearSessionInfo();
        if (reason === ErrorReason.Unauthorized || reason === ErrorReason.Unknown) {
          switch (authType) {
            case AuthenticationType.Sspi:
              reason = LoginErrorCodes.SspiFailed;
              break;
            case AuthenticationType.Kerberos:
              reason = LoginErrorCodes.KerberosFailed;
              break;
            case AuthenticationType.ClientCertificate:
            case AuthenticationType.ClientCertificateWithFallback:
              reason = LoginErrorCodes.ClientCertificateNotFound
              break;
          }
        }
        return this.$q.reject(reason);
      });
    })
  }

  public initialize(username: string, password: string, displayName: string): ng.IPromise<void> {
    return this.sessionInfoRequest(() => {
      return this.requestPost(new GeneratePublicKeyRequest())
      .then((generatePublicKeyResult: GeneratePublicKeyResult) => {
        var encryptedPassword = rsa.encrypt(password, generatePublicKeyResult.key);
        var initializeRequest: VizPortalClientRequest<LoginParams, SessionInfoResult>;

        if (this.isServerDomainFamilyActiveDirectory()) {
          initializeRequest = new InitializeServerWithActiveDirectoryUserRequest({
            username: username,
            encryptedPassword: encryptedPassword,
            keyId: generatePublicKeyResult.keyId
          });
        } else if (this.isServerDomainFamilyLocal()) {
          initializeRequest = new InitializeServerWithLocalUserRequest({
            username: username,
            encryptedPassword: encryptedPassword,
            keyId: generatePublicKeyResult.keyId,
            displayName: displayName
          });
        } else if (this.isServerDomainFamilyExternal()) {
          initializeRequest = new InitializeServerWithExternalUserRequest({
            username: username,
            encryptedPassword: encryptedPassword,
            keyId: generatePublicKeyResult.keyId
          });
        } else {
          throw new Error('Unknown domain family type. Current implementation only supports: AD, local, and external.');
        }
        return this.requestPost(initializeRequest)
        .then((initializeResult: SessionInfoResult) => {
          if (initializeResult.errors) {
            return this.$q.reject(initializeResult.errors[0] && initializeResult.errors[0].code);
          }
          return this.setSessionInfo(initializeResult);
        });
      });
    });
  }

  public switchSiteIfNeeded(urlName: string, podRedirectPath?: string): ng.IPromise<any> {
    return this.sessionInfoRequest(() => {
      // falsy values should be empty string for default site
      urlName = urlName || '';

      if (!this.isCurrentSiteUrlName(urlName)) {

        return this.requestPost(new SwitchSiteRequest({urlName : urlName}))
        .then((sessionInfo: SessionInfoResult) => {
          if (sessionInfo && sessionInfo.errors) {
            var error = sessionInfo.errors[0];
            if (error && error.code === SiteSwitchErrorCodes.AttemptedToSwitchSiteToCurrentSite) {
              // No-op, do nothing
              // Remember that this particular site url name is case insensitive
              this.caseInsensitiveSiteUrlNames.add(urlName);
              this.refreshSessionInfo(); // TFSID: 503672 refresh in case stale data caused this
              return;
            }
            return this.$q.reject(error);
          }
          this.setSessionInfo(sessionInfo);
        }, (error: IError) => {
          if (error && error.code === CommonErrorCode.RedirectToOtherPod) {
            this.redirectToOtherPod(error, podRedirectPath);
            // If redirected, defer indefinitely, to prevent further execution here
            return this.$q.defer().promise;
          }
        });
      }
      return this.$q.when(null);
    });
  }
   */
}
